import { setError } from 'actionCreators/error';
import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { of } from 'rxjs';
import { map } from 'rxjs/operators';
import { API_V4_BASE_URL, BUILD_NUMBER, GOOGLE_ANALYTICS_ID } from 'settings';
import { getUrlParams } from 'utils';
import { getStore } from '../redux/store';
import { userIdentified } from 'services/socket';

declare let gtag: (command: string, commandOrValue: any | string, value?: any) => void;

class TokenService {
  private readonly TOKEN: string = 'newAuthToken';
  private readonly TOKEN_EXPIRATION: string = 'tokenExp';
  private readonly REFRESH_TOKEN: string = 'refreshToken';

  private refreshing$: Promise<string>;
  private instance: AxiosInstance = axios.create();

  constructor() {
    const tokenServiceInstance = this;
    this.instance.interceptors.response.use(
      (response) => {
        const token = response.headers['x-token'];
        tokenServiceInstance.updateToken(token);

        return response;
      },
      (error) => {
        const { dispatch } = getStore();
        if (error.response && error.response.status === 401) {
          localStorage.removeItem(tokenServiceInstance.TOKEN);
          localStorage.removeItem(tokenServiceInstance.REFRESH_TOKEN);

          dispatch({ type: 'LOGOUT' });
        }
        if (!error.response) {
          dispatch(setError(504));
        }
        return Promise.reject(error);
      }
    );
  }

  public updateToken(token: string, refreshToken: string = null) {
    if (token && token !== 'invalid' && localStorage.getItem(this.TOKEN) !== token) {
      const claims = this.parseJwt(token);
      const tokenTtl = claims.exp - claims.iat;
      const tokenExpiration = new Date();
      //Forcing the token to expire 120 seconds earlier
      tokenExpiration.setSeconds(tokenExpiration.getSeconds() + tokenTtl - 120);
      localStorage.setItem(this.TOKEN, token);
      localStorage.setItem(this.TOKEN_EXPIRATION, tokenExpiration.getTime().toString());
      if (refreshToken) {
        localStorage.setItem(this.REFRESH_TOKEN, refreshToken);
      }
    }

    if (token === 'invalid') {
      localStorage.removeItem(this.TOKEN);
      localStorage.removeItem(this.REFRESH_TOKEN);

      const { dispatch } = getStore();
      dispatch({ type: 'LOGOUT' });
    }
  }

  public async getToken(): Promise<string> {
    return of(localStorage.getItem(this.TOKEN))
      .pipe(
        map(async (authToken) => {
          const exp = this.getExpiration();
          const currentTime = new Date().getTime() / 1000;
          if ((exp === 0 || (exp && exp < currentTime)) && !!localStorage.getItem(this.REFRESH_TOKEN)) {
            return this.refreshToken();
          }
          return authToken;
        })
      )
      .toPromise();
  }

  public isAuthenticated(): boolean {
    const { search } = window.location;
    const recoveryToken = new URLSearchParams(search).get('accounts_recovery_token');

    if (recoveryToken) {
      localStorage.removeItem(this.TOKEN);
      localStorage.removeItem(this.REFRESH_TOKEN);
      return false;
    }

    const userSession = this.getUserSessionCookie();
    if (userSession?.token && this.parseJwt(userSession?.token)?.is_support) {
      if (localStorage.getItem(this.TOKEN) !== userSession.token) {
        localStorage.removeItem(this.TOKEN_EXPIRATION);
      }
      localStorage.setItem(this.TOKEN, userSession.token);
      localStorage.setItem(this.REFRESH_TOKEN, userSession.refresh_token);
      this.setUserByToken(userSession.token);
      return true;
    }

    if (localStorage.getItem(this.TOKEN) !== null) {
      const bearerToken = localStorage.getItem(this.TOKEN);
      this.setUserByToken(bearerToken);
      return true;
    }

    const { token, refreshToken } = getUrlParams();

    if (token && refreshToken) {
      localStorage.setItem(this.TOKEN, token);
      localStorage.setItem(this.REFRESH_TOKEN, refreshToken);
      return true;
    }
    if (!!localStorage.getItem(this.TOKEN)) {
      return true;
    }
    return false;
  }

  public getRefreshToken(): string {
    return localStorage.getItem(this.REFRESH_TOKEN);
  }

  public getTenantId(): number {
    const token = localStorage.getItem(this.TOKEN);
    if (!token) {
      return null;
    }
    return this.parseJwt(token)?.tenant_id;
  }

  public setUserByToken(parsedToken: string) {
    try {
      const token = this.parseJwt(parsedToken);
      userIdentified(token);
      gtag('set', { user_id: token.id });
      gtag('config', GOOGLE_ANALYTICS_ID, { page_path: document.location.pathname });
    } catch (error) {
      gtag('set', { user_id: null });
    }
  }

  public parseJwt(token: string) {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join('')
    );

    return JSON.parse(jsonPayload);
  }

  private getUserSessionCookie(): any {
    return document.cookie.split('; ').reduce((acc, cookie) => {
      const parts = cookie.split('=');
      try {
        acc[parts[0]] = JSON.parse(decodeURIComponent(parts[1]));
      } catch (err) {
        acc[parts[0]] = parts[1];
      }
      return acc;
    }, {} as any)?.userSession;
  }

  private getExpiration(): number {
    const exp = Number(localStorage.getItem(this.TOKEN_EXPIRATION));
    if (exp > 0) {
      return exp / 1000;
    }
    return 0;
  }

  private refreshToken(): Promise<string> {
    if (!this.refreshing$) {
      this.refreshing$ = this.refresh();
    }

    return this.refreshing$;
  }

  private async refresh(): Promise<string> {
    const refreshToken = localStorage.getItem(this.REFRESH_TOKEN);
    const currentToken = localStorage.getItem(this.TOKEN);
    const config: AxiosRequestConfig = {
      method: `post`,
      baseURL: API_V4_BASE_URL,
      url: '/oauth/refresh',
      withCredentials: true,
      headers: {
        RefreshToken: refreshToken,
        Authorization: `Bearer ${currentToken}`,
        FrontVersion: BUILD_NUMBER || 'local',
      },
      validateStatus: (status: number) => status >= 100 && status <= 404,
    };
    const response: any = await this.instance(config);
    const token = response.data?.data?.token.replace('Bearer ', '') || 'invalid';
    this.updateToken(token);
    this.refreshing$ = null;
    return localStorage.getItem(this.TOKEN);
  }
}

export default new TokenService();
